import { observer } from 'mobx-react';

import { WidgetCrownBadge } from '../../../features/widgets/components/JackpotWidget/JackpotWidgetArt';
import { useStores } from '../../../stores/useStores';
import { CoinWithValue } from '../../CoinWithValue/CoinWithValue';

import styles from './CrownJackpotBadge.module.scss';

/**
 * Crown Jackpot badge on a lobby game tile — frame `9787:5634`, the badge group at +10/+16 inside the
 * `Thumbnail` component `10083:13431`. Every measurement is in the SCSS header.
 *
 * A crown badge overlapping the left end of a pill that carries the coin and the top level pot — the
 * biggest prize on the ladder, by association priority. Same slot the legacy Crownslots
 * `JackpotBalance` badge uses, and mutually exclusive with it: a tile shows one jackpot or the other,
 * never both stacked.
 *
 * ## Visibility is not decided here
 * WHETHER this is drawn is CSS — see the module's `.host` rules. The caller renders it for any game
 * whose `jackpots` array is non-empty, and two `<body>` attributes (`data-jp-badges`, `data-jp-coin`,
 * both published by `JackpotStore`) decide whether the tile's own `data-jackpot-coins` matches.
 *
 * That matters because a game is normally enrolled on BOTH coins while only one jackpot is live, and
 * because the gate flips on maintenance and on the widget's pacing decision. Reading either from the
 * store here would make every tile in the lobby an observer, re-rendering the lot to change nothing
 * but `display`.
 *
 * What is still read here is the AMOUNT — a live figure that genuinely changes on the 30s pot poll,
 * and the one thing on this badge that has to come from the store.
 */
function CrownJackpotBadgeComponent() {
  const { coinTypeStore, jackpotStore } = useStores();

  const { topLevelAmountLabel } = jackpotStore;

  return (
    /*
      The host exists to be the query container, and it is pulled out to the card's BORDER box so that
      one `cqi` unit reproduces the frame's 336 design pixels. It carries the test id because it is the
      element the tile owns; everything measured hangs off `.badge` inside it.
    */
    <div className={styles.host} data-testid="crown-jackpot-tile-badge">
      <div className={styles.badge}>
        <CoinWithValue
          className={styles.amount}
          coinSide="left"
          coinType={coinTypeStore.coinType}
          valueClassName={styles.amountValue}
        >
          {/*
            Rounded ("487.2M"), like every other Crown Jackpot surface — the store's label owns the
            rule. Blank rather than 0 when the total is unknown: a jackpot advertising nothing is
            worse than one advertising no figure.
          */}
          {topLevelAmountLabel}
        </CoinWithValue>

        <WidgetCrownBadge className={styles.crown} />
      </div>
    </div>
  );
}

export const CrownJackpotBadge = observer(CrownJackpotBadgeComponent);
